import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Plus, Minus, Search, X, UtensilsCrossed, MapPin } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useData } from '../../context/DataContext';
import { useCart } from '../../context/CartContext';
import { useTable } from '../../context/TableContext';
import { useAuth } from '../../context/AuthContext';

const CustomerHome: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { products, tables, updateTables } = useData();
  const { cart, addToCart, removeFromCart, getCartItemCount, getCartTotal, getCartItems } = useCart();
  const { selectedTable, selectTable } = useTable();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [showCartModal, setShowCartModal] = useState(false);
  const [showTableSelectionModal, setShowTableSelectionModal] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [selectedTableIdForModal, setSelectedTableIdForModal] = useState('');
  const [customerSession, setCustomerSession] = useState<any>(null);

  // Load customer session data
  useEffect(() => {
    const session = localStorage.getItem('bookfood_customer_session');
    if (session) {
      try {
        const sessionData = JSON.parse(session);
        setCustomerSession(sessionData);
        setCustomerName(sessionData.name || '');
      } catch (error) {
        console.error('Error parsing customer session:', error);
      }
    }
  }, []);

  // Check if table is selected on mount
  useEffect(() => {
    if (!selectedTable && customerSession?.orderType === 'dine-in') {
      setShowTableSelectionModal(true);
    }
  }, [selectedTable, customerSession]);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ['All', ...Array.from(new Set(products.map(p => p.category)))];

  const ProductCard: React.FC<{ product: any; index: number }> = ({ product, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="food-card hover:shadow-xl flex flex-col h-full"
    >
      <img
        src={product.image}
        alt={product.name}
        className="w-full h-40 sm:h-48 rounded-xl object-cover mb-3"
      />
      <div className="flex-1 flex flex-col">
        <h3 className="text-white font-semibold text-base sm:text-lg leading-tight mb-2">{product.name}</h3>
        <p className="text-gray-400 text-xs sm:text-sm leading-relaxed line-clamp-2 mb-2 flex-1">
          {product.description}
        </p>
        <span className="inline-block px-2 sm:px-3 py-1 bg-orange-500 bg-opacity-20 text-orange-400 text-xs rounded-full mb-3 w-fit">
          {product.category}
        </span>
        <div className="flex items-end justify-between mt-auto">
          <div>
            <p className="text-orange-400 font-bold text-xl sm:text-2xl">${product.price}</p>
            <p className="text-gray-500 text-xs">Stock: {product.stock}</p>
          </div>
          <div className="flex-shrink-0">
            {cart[product.id] ? (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => removeFromCart(product.id)}
                  className="w-8 h-8 sm:w-9 sm:h-9 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-all"
                >
                  <Minus size={16} />
                </button>
                <span className="text-white font-bold w-6 sm:w-8 text-center text-sm sm:text-base">{cart[product.id]}</span>
                <button
                  onClick={() => addToCart(product.id, products)}
                  className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-full flex items-center justify-center transition-all"
                >
                  <Plus size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => addToCart(product.id, products)}
                className="px-3 sm:px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-xs sm:text-sm font-semibold rounded-lg transition-all"
              >
                Add to Cart
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-lg bg-slate-900 bg-opacity-80 border-b border-gray-800">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                <UtensilsCrossed className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
                  Hotel Hub
                </h1>
                {(customerSession?.name || customerName) && (
                  <p className="text-xs sm:text-sm text-gray-400">
                    Welcome, {customerSession?.name || customerName}!
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <button 
                onClick={() => setShowCartModal(true)}
                className="p-2 sm:p-3 text-gray-400 hover:text-white relative transition-colors rounded-full hover:bg-slate-800"
              >
                <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6" />
                {getCartItemCount() > 0 && (
                  <span className="absolute -top-1 -right-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center font-bold">
                    {getCartItemCount()}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Customer Information Banner */}
        {(customerSession || selectedTable) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="food-card"
          >
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-gray-400 text-xs mb-1 uppercase tracking-wide">Customer Name</p>
                <h3 className="text-white font-bold text-lg">
                  {customerSession?.name || selectedTable?.currentCustomerName || 'Guest'}
                </h3>
              </div>
              {selectedTable && (
                <div>
                  <p className="text-gray-400 text-xs mb-1 uppercase tracking-wide">Table Number</p>
                  <h3 className="text-white font-bold text-lg">{selectedTable.tableNumber}</h3>
                </div>
              )}
              {customerSession?.orderType === 'delivery' && (
                <div>
                  <p className="text-gray-400 text-xs mb-1 uppercase tracking-wide">Order Type</p>
                  <h3 className="text-white font-bold text-lg">Delivery</h3>
                </div>
              )}
            </div>
            <div className="pt-3 border-t border-gray-700">
              {selectedTable ? (
                <p className="text-gray-400 text-sm">{selectedTable.location} • {selectedTable.capacity} seats</p>
              ) : customerSession?.orderType === 'delivery' && customerSession?.address ? (
                <p className="text-gray-400 text-sm">Delivery to: {customerSession.address}</p>
              ) : (
                <p className="text-gray-400 text-sm">Guest Customer • {customerSession?.phone || 'No phone'}</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative"
        >
          <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 sm:w-6 sm:h-6" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 bg-slate-800 border-2 border-gray-700 rounded-xl sm:rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base transition-all"
            placeholder="Search for delicious food..."
          />
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex space-x-2 sm:space-x-3 overflow-x-auto pb-2 scrollbar-hide"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-all duration-200 ${
                selectedCategory === category
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                  : 'bg-slate-800 text-gray-300 hover:bg-slate-700'
              }`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* Products Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="w-full"
        >
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Menu</h2>
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {filteredProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        </motion.div>

        {/* Cart Summary */}
        {getCartItemCount() > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-32 sm:bottom-32 left-1/2 transform -translate-x-1/2 w-full max-w-md px-3 sm:px-4 z-40"
          >
            <div className="food-card shadow-2xl">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm sm:text-base md:text-lg truncate">{getCartItemCount()} items in cart</p>
                  <p className="text-orange-400 font-bold text-base sm:text-lg md:text-xl">Total: ${getCartTotal(products).toFixed(2)}</p>
                </div>
                <motion.button
                  onClick={() => setCurrentScreen('customer-checkout')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary text-sm sm:text-base whitespace-nowrap"
                >
                  Checkout
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Cart Modal */}
      {showCartModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
          onClick={() => setShowCartModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-slate-800 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gradient-to-r from-orange-500 to-red-500">
              <h2 className="text-2xl font-bold text-white">Shopping Cart</h2>
              <button
                onClick={() => setShowCartModal(false)}
                className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Cart Items */}
            <div className="p-6 max-h-96 overflow-y-auto">
              {getCartItems(products).length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart size={64} className="mx-auto text-gray-600 mb-4" />
                  <p className="text-gray-400 text-lg">Your cart is empty</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {getCartItems(products).map(({ product, quantity }) => (
                    product && (
                      <div key={product.id} className="flex items-center space-x-4 bg-slate-700 rounded-xl p-4 hover:bg-slate-600 transition-colors">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-20 h-20 rounded-xl object-cover"
                        />
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-lg">{product.name}</h3>
                          <p className="text-gray-400">${product.price} each</p>
                        </div>
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => removeFromCart(product.id)}
                            className="w-10 h-10 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors"
                          >
                            <Minus size={18} />
                          </button>
                          <span className="text-white font-bold w-10 text-center text-lg">{quantity}</span>
                          <button
                            onClick={() => addToCart(product.id, products)}
                            className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-full flex items-center justify-center transition-colors"
                          >
                            <Plus size={18} />
                          </button>
                        </div>
                      </div>
                    )
                  ))}
                </div>
              )}
            </div>

            {/* Cart Footer */}
            {getCartItems(products).length > 0 && (
              <div className="p-6 border-t border-gray-700 bg-slate-900">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white font-semibold text-lg">Total ({getCartItemCount()} items)</span>
                  <span className="text-orange-400 font-bold text-2xl">${getCartTotal(products).toFixed(2)}</span>
                </div>
                <motion.button
                  onClick={() => {
                    setShowCartModal(false);
                    setCurrentScreen('customer-checkout');
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full btn-primary text-lg py-4"
                >
                  Proceed to Checkout
                </motion.button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}

      <BottomNavigation />

      {/* Table Selection Modal */}
      {showTableSelectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-slate-800 rounded-2xl w-full max-w-md overflow-hidden"
          >
            <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-center">
              <MapPin size={48} className="mx-auto text-white mb-3" />
              <h2 className="text-2xl font-bold text-white mb-2">Select Your Table</h2>
              <p className="text-white text-sm opacity-90">Please select a table and enter your name</p>
            </div>
            <div className="p-6 space-y-4">
              {/* Customer Name Input */}
              <div>
                <label className="block text-white text-sm font-semibold mb-2">Your Name</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-700 border-2 border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter your name"
                />
              </div>

              {/* Table Selection */}
              <div>
                <label className="block text-white text-sm font-semibold mb-2">Select Table</label>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {tables.filter(t => t.status === 'available').map((table) => (
                    <motion.button
                      key={table.id}
                      onClick={() => setSelectedTableIdForModal(table.id)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`w-full p-4 rounded-xl text-left transition-colors ${
                        selectedTableIdForModal === table.id
                          ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                          : 'bg-slate-700 hover:bg-slate-600 text-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-bold text-lg">{table.tableNumber}</h3>
                          <p className="text-sm opacity-90">{table.location}</p>
                          <p className="text-xs opacity-75 mt-1">{table.capacity} seats</p>
                        </div>
                        <div className="text-3xl">🪑</div>
                      </div>
                    </motion.button>
                  ))}
                </div>
                {tables.filter(t => t.status === 'available').length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No tables available at the moment</p>
                    <p className="text-gray-500 text-sm mt-2">Please contact staff for assistance</p>
                  </div>
                )}
              </div>

              {/* Confirm Button */}
              <motion.button
                onClick={async () => {
                  if (!customerName.trim()) {
                    alert('Please enter your name');
                    return;
                  }
                  if (!selectedTableIdForModal) {
                    alert('Please select a table');
                    return;
                  }

                  // Update table status to occupied and assign customer
                  const updatedTables = tables.map(t =>
                    t.id === selectedTableIdForModal
                      ? {
                          ...t,
                          status: 'occupied' as const,
                          currentCustomerName: customerName.trim(),
                          currentCustomerId: user?.customerId || user?.id || 'unknown',
                          sessionStartTime: new Date().toISOString()
                        }
                      : t
                  );

                  await updateTables(updatedTables);
                  selectTable(selectedTableIdForModal);
                  setShowTableSelectionModal(false);
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={!customerName.trim() || !selectedTableIdForModal}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 disabled:from-gray-600 disabled:to-gray-600 text-white font-bold py-4 rounded-xl transition-all disabled:cursor-not-allowed"
              >
                Confirm Selection
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CustomerHome;
